// import packages
import React from 'react'

// import apis
import { apiGetUsers, apiEmulate } from 'api/user'

// import redux, selectors, actions
import { useDispatch } from 'react-redux'
import { emulatingUser } from 'redux/actions/memory'

// import contexts
import { AuthContext } from 'contexts/AuthContext'
import { FetchContext } from 'contexts/FetchContext'

// import hooks

// import utilities

// import common elements
import { Table, Button, Form } from 'react-bootstrap'
import { PageLayout } from 'components/elements/Layout'
import { Icon } from 'components/elements/Icons'

// import components
import AuthenticatedNav from 'components/navs/AuthenticatedNav'

// import styles

// local constants

// --------------------

const AdminUsers = () => {
  const { authAxios } = React.useContext(FetchContext)
  const { setAuthState } = React.useContext(AuthContext)

  const printRequestQueueActive = process.env.PRINT_REQUEST_QUEUE_ACTIVE === 'true'

  const reduxDispatch = useDispatch()

  const [sortBy, setSortBy] = React.useState('latest')
  const [sortAsc, setSortAsc] = React.useState(false)
  const [searchText, setSearchText] = React.useState('')

  const [users, setUsers] = React.useState([])
  const [loadedUsers, setLoadedUsers] = React.useState(null)

    const userCount = users.length
  const userCountByYear = users.reduce((acc, user) => {
    const year = new Date(user.latest_login).getFullYear()
    acc[year] = (acc[year] || 0) + 1
    return acc
  }, {})
  const sortedYears = Object.keys(userCountByYear).sort((a, b) => b - a)

  React.useEffect(() => {
    const abortController = new AbortController()

    if (!loadedUsers) {
      apiGetUsers(authAxios, {
        signal: abortController.signal,
        success: response => {
          setUsers(response)
          setLoadedUsers(true)
        },
      })
    }
    return () => {
      abortController.abort()
    }
  }, [loadedUsers, authAxios])

  React.useEffect(() => {
    setSortAsc(sortBy === 'latest' ? false : true)
  }, [sortBy])

  const emulate = id => {
    apiEmulate(authAxios, id, {
      success: setAuthState,
      successDispatch: [reduxDispatch, emulatingUser],
      successMsg: 'Emulating',
    })
  }

  const [DOWN, UP] = sortAsc ? [-1, 1] : [1, -1]

  const sortName = () => {
    if (sortBy === 'name') {
      setSortAsc(t => !t)
    } else {
      setSortBy('name')
    }
  }
  const sortEmail = () => {
    if (sortBy === 'email') {
      setSortAsc(t => !t)
    } else {
      setSortBy('email')
    }
  }
  const sortCount = () => {
    if (sortBy === 'count') {
      setSortAsc(t => !t)
    } else {
      setSortBy('count')
    }
  }
  const sortLatest = () => {
    if (sortBy === 'latest') {
      setSortAsc(t => !t)
    } else {
      setSortBy('latest')
    }
  }

  const sortCreated = () => {
    if (sortBy === 'created') {
      setSortAsc(t => !t)
    } else {
      setSortBy('created')
    }
  }

  // sorting functions

  const nameSort = (a, b) => {
    const f1 = a.first_name
    const f2 = b.first_name
    const l1 = a.last_name
    const l2 = b.last_name
    return l1 < l2 ? DOWN : l1 > l2 ? UP : f1 < f2 ? DOWN : f1 > f2 ? UP : 0
  }
  const emailSort = (a, b) => {
    const c1 = a.email
    const c2 = b.email
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }

  const cardSort = (a, b) => {
    const c1 = a.cards_count
    const c2 = b.cards_count
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }

  const latestSort = (a, b) => {
    const l1 = a.latest_login || ''
    const l2 = b.latest_login || ''
    return l1 < l2 ? DOWN : l1 > l2 ? UP : 0
  }

  const createdSort = (a, b) => {
    const c1 = a.created_at || ''
    const c2 = b.created_at || ''
    return c1 < c2 ? DOWN : c1 > c2 ? UP : 0
  }

  const currentSorter = () => {
    if (sortBy === 'name') {
      return nameSort
    } else if (sortBy === 'email') {
      return emailSort
    } else if (sortBy === 'count') {
      return cardSort
    } else if (sortBy === 'created') {
      return createdSort
    } else {
      return latestSort
    }
  }

  const matchesSearch = (user) => {
    const fullName = `${user.first_name} ${user.last_name}`.toLowerCase()
    const email = user.email.toLowerCase()
    const search = searchText.toLowerCase()
    return (
      fullName.includes(search) || email.includes(search)
    )
  }

  const searchCount = users.filter(u => matchesSearch(u)).length
  const filteredUsers = users.filter(u => matchesSearch(u))
  const sortedUsers = filteredUsers.sort(currentSorter())

  const sortIcon = field => {
    return field === sortBy ? (
      sortAsc ? (
        <Icon spaceAfter icon='up' />
      ) : (
        <Icon spaceAfter icon='down' />
      )
    ) : null
  }
  
  const reload = () => {
    setLoadedUsers(null)
  }

  const actions = [
    <Button
      key='reload'
      disabled={loadedUsers === null}
      onClick={reload}
      variant='outline-secondary'
      size='sm'
    >
      Reload
    </Button>
  ]

  return (
    <PageLayout title='Users' actions={actions}>
      <AuthenticatedNav />
      <p>
        Users by year last logged in: {sortedYears.map(year => (
        <span key={year}>{year === '2025' ? '' : ', '}{year}: {userCountByYear[year]}</span>
        ))}</p>
      <div className='d-flex justify-flex-start align-items-baseline mt-0' style={{ gap: '1rem' }}>
        <div>

            <Form.Control
              name='search'
                type='text'
                size='sm'
              placeholder='Search...'
              onChange={e => setSearchText(e.target.value)}
              value={searchText}
            />

          </div>
        <div>
          <Button
            size='sm'
            variant='outline-secondary'
            onClick={() => setSearchText('')}
          >
            Clear Search
          </Button>
          </div>
          <div>Matches: {searchCount}/{userCount}</div>
      </div>
 
  
      {loadedUsers && (
        <Table striped bordered hover size='sm'>
          <thead>
            <tr>
              <th onClick={sortName}>{sortIcon('name')}Name</th>
              <th onClick={sortEmail}>{sortIcon('email')}Email</th>
              <th onClick={sortCount} className='text-center'>
                {sortIcon('count')}Cards
              </th>
              <th onClick={sortCreated}>{sortIcon('created')}Created</th>
              <th onClick={sortLatest}>{sortIcon('latest')}Latest Login</th>
              {printRequestQueueActive && <th>Print Request Counts</th>}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => (
              <tr key={u.id}>
                <td>
                  <Button variant='link'onClick={() => emulate(u.id)}>{u.first_name} {u.last_name}</Button>
                </td>
                <td>{u.email}</td>
                <td className='text-center'>{u.cards_count}</td>
                <td>{u.created}</td>
                <td>{u.last_login}</td>
                {printRequestQueueActive && <td className='text-center'>p<strong>{u.pr_pending_count}</strong>  d<strong>{u.pr_downloaded_count}</strong> | r<strong>{u.pr_ready_count}</strong> | x<strong>{u.pr_denied_count}</strong> | c<strong>{u.pr_complete_count}</strong></td>}

           
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </PageLayout>
  )
}

export default AdminUsers
